// Custom hook for resizing functionality
import { useCallback, useEffect, useRef, useState } from 'react';

export function useResizable(initialHeight: number, minHeight: number = 200, maxHeight: number = 600, onHeightChange?: (height: number) => void) {
    const [height, setHeight] = useState(initialHeight);
    const [isResizing, setIsResizing] = useState(false);
    const startY = useRef(0);
    const startHeight = useRef(0);

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            setIsResizing(true);
            startY.current = e.clientY;
            startHeight.current = height;
            document.body.style.cursor = 'ns-resize';
            document.body.style.userSelect = 'none';
        },
        [height]
    );

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isResizing) return;

            const deltaY = e.clientY - startY.current;
            const newHeight = Math.min(maxHeight, Math.max(minHeight, startHeight.current + deltaY));

            setHeight(newHeight);
            onHeightChange?.(newHeight);
        },
        [isResizing, minHeight, maxHeight, onHeightChange]
    );

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }, []);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isResizing, handleMouseMove, handleMouseUp]);

    return {
        height,
        setHeight,
        isResizing,
        handleMouseDown,
    };
}
